// Inline SVG icons. Deliberately hand-rolled rather than pulling in an icon
// package: the console needs a handful of glyphs and the bundle stays dependency
// free (same rationale as the toast system replacing react-toastify).
//
// Every icon is purely decorative — it is always rendered inside a control that
// carries the accessible name (IconButton's `label` → aria-label + title). So
// `Icon` marks the <svg> `aria-hidden` and `focusable="false"`: screen readers
// announce the button's label rather than the glyph, and the SVG never becomes a
// tab stop of its own.

import type { ReactNode } from "react"

function Icon({ children, size = 16 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

/** Key — the role's permissions. */
export function KeyIcon() {
  return (
    <Icon>
      <circle cx="8" cy="15" r="4" />
      <path d="M10.85 12.15 19 4" />
      <path d="M18 5l2 2" />
      <path d="M15 8l2 2" />
    </Icon>
  )
}

/** Pencil — edit. */
export function PencilIcon() {
  return (
    <Icon>
      <path d="M4 20h4l10.5-10.5a2.12 2.12 0 0 0-3-3L5 17v3z" />
      <path d="M13.5 6.5l4 4" />
    </Icon>
  )
}

/** Trash — delete (destructive). */
export function TrashIcon() {
  return (
    <Icon>
      <path d="M4 7h16" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M5 7l1 13h12l1-13" />
      <path d="M9 7V4h6v3" />
    </Icon>
  )
}

/** Plus — assign / add. */
export function PlusIcon() {
  return (
    <Icon>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </Icon>
  )
}

/** Cross — remove an assigned item. Smaller: it sits inside a chip. */
export function CrossIcon() {
  return (
    <Icon size={12}>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </Icon>
  )
}

/** Users — a team's members (the composition drawer). */
export function UsersIcon() {
  return (
    <Icon>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16 6a3 3 0 0 1 0 5.5" />
      <path d="M17.5 14.5A6 6 0 0 1 21 20" />
    </Icon>
  )
}

/** Sun — the light theme. */
export function SunIcon() {
  return (
    <Icon>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Icon>
  )
}

/** Moon — the dark theme. */
export function MoonIcon() {
  return (
    <Icon>
      <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />
    </Icon>
  )
}

/** Magnifier — the filter-bar search field. Purely decorative (the input owns
    its accessible name), so it inherits Icon's aria-hidden treatment. */
export function SearchIcon() {
  return (
    <Icon>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </Icon>
  )
}

/** Sign-out — door with an arrow leaving it. */
export function LogoutIcon() {
  return (
    <Icon>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </Icon>
  )
}
